import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { formatName } from '@/utils/nameFormatter';

/**
 * WorkloadChart
 * Stacked bar chart showing task distribution per team member.
 * Built with pure CSS/HTML — no external chart library required.
 * Data is derived from the tasks array prop.
 */

const STATUS_COLORS = {
    Backlog: 'bg-slate-200',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
};

const LEGEND = [
    { label: 'Backlog', color: 'bg-slate-300' },
    { label: 'In Progress', color: 'bg-[#28b5fd]' },
    { label: 'Review', color: 'bg-amber-300' },
    { label: 'Completed', color: 'bg-emerald-400' },
    { label: 'On Hold', color: 'bg-orange-300' },
    { label: 'Cancelled', color: 'bg-red-300' },
];

// Build full display name combining name + surname (Admin model stores them separately)
// Returns the name in Title Case (first letter of each word capitalised).
function getFullName(person) {
    if (!person) return 'Unassigned';
    if (person.fullName) return formatName(person.fullName);
    const parts = [person.name, person.surname].filter(Boolean);
    const full = parts.join(' ').trim() || person.name || 'Unassigned';
    return formatName(full);
}

function buildWorkloadData(tasks) {
    const map = {};

    tasks.forEach((task) => {
        const fullName = getFullName(task.assignee);
        const id = task.assignee?._id || 'unassigned';

        if (!map[id]) {
            map[id] = {
                id,
                name: fullName,
                Backlog: 0,
                'In Progress': 0,
                'Under Review': 0,
                Completed: 0,
                'On Hold': 0,
                Cancelled: 0,
                total: 0,
            };
        }

        const normStatus = (task.status === 'Closed' || task.status === 'Close') ? 'Completed' : task.status;
        map[id][normStatus] = (map[id][normStatus] || 0) + 1;
        map[id].total += 1;
    });

    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 8);
}

export default function WorkloadChart({ tasks = [], loading = false }) {
    const workloadData = useMemo(() => buildWorkloadData(tasks), [tasks]);
    const maxTotal = Math.max(...workloadData.map((d) => d.total), 1);

    if (loading) {
        return (
            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] p-6 lg:col-span-2 flex items-center justify-center min-h-[280px]">
                <Spin size="large" />
            </div>
        );
    }

    if (!workloadData.length) {
        return (
            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] p-6 lg:col-span-2 flex items-center justify-center min-h-[280px] flex-col">
                <p className="text-sm text-[#424750] dark:text-slate-400 font-medium">
                    No task data yet — create tasks and assign them to see workload here.
                </p>
            </div>
        );
    }

    // BAR_MAX_HEIGHT is now driven by the flex container — bars scale to fill
    // whatever height the card has (which matches the AIAlertsPanel beside it).
    const BAR_MAX_HEIGHT_PX = 160; // used only to scale individual bar heights proportionally

    return (
        <div data-tour="workload-chart" className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] p-5 lg:col-span-2 flex flex-col">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                <div>
                    <h2 className="text-xl font-bold text-[#003263] dark:text-[#a7c8ff] font-headline">
                        Workload Distribution
                    </h2>
                    <p className="text-[#424750] dark:text-slate-400 text-sm font-medium">
                        Task segmentation per team member
                    </p>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-wider text-[#424750] dark:text-slate-400">
                    {LEGEND.map((l) => (
                        <div key={l.label} className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${l.color}`} />
                            {l.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart — flex-1 makes this region fill the card's remaining height */}
            <div className="flex gap-4 flex-1 mt-4 min-h-0">
                {/* Y-axis labels — stretch to fill the chart height */}
                <div className="flex flex-col justify-between text-xs font-bold text-slate-400 pb-8 pr-2 border-r border-slate-100 dark:border-slate-700 flex-shrink-0">
                    {[maxTotal, Math.ceil(maxTotal * 0.75), Math.ceil(maxTotal * 0.5), Math.ceil(maxTotal * 0.25), 0].map((v) => (
                        <span key={v}>{v}</span>
                    ))}
                </div>

                {/* Bars — align to bottom of the flex container */}
                <div className="flex-1 flex items-end justify-around px-2 pb-0">
                    {workloadData.map((member) => {
                        const barHeight = Math.round((member.total / maxTotal) * BAR_MAX_HEIGHT_PX);
                        const statuses = ['Cancelled', 'On Hold', 'Completed', 'Under Review', 'In Progress', 'Backlog'];

                        return (
                            <div key={member.id} className="flex flex-col items-center w-full max-w-[48px] group cursor-pointer">
                                {/* Stacked bar */}
                                <div
                                    className="w-full flex flex-col-reverse rounded-t-lg overflow-hidden transition-all group-hover:scale-105 shadow-sm"
                                    style={{ height: barHeight || 4 }}
                                >
                                    {statuses.map((status) => {
                                        const count = member[status] || 0;
                                        if (!count) return null;
                                        const pct = Math.round((count / member.total) * 100);
                                        return (
                                            <div
                                                key={status}
                                                className={STATUS_COLORS[status]}
                                                style={{ height: `${pct}%` }}
                                                title={`${status}: ${count}`}
                                            />
                                        );
                                    })}
                                </div>

                                {/* Name label */}
                                <span className="mt-3 text-xs font-bold text-[#191c1d] dark:text-white text-center leading-tight max-w-[56px] break-words">
                                    {member.name}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer stats */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
                <div className="flex gap-10">
                    <div>
                        <p className="text-xs font-bold text-[#424750] dark:text-slate-400 uppercase tracking-widest">
                            Total Tasks
                        </p>
                        <p className="text-lg font-bold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            {tasks.length}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-[#424750] dark:text-slate-400 uppercase tracking-widest">
                            Assigned Members
                        </p>
                        <p className="text-lg font-bold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            {workloadData.filter((d) => d.id !== 'unassigned').length}
                        </p>
                    </div>
                </div>
                {/* "View Productivity Report" link — matches approved design */}
                <a
                    href="#"
                    className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline flex items-center gap-1 transition-all"
                >
                    View Productivity Report
                    <span style={{ fontSize: 14 }}>→</span>
                </a>
            </div>
        </div>
    );
}
